import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import herobentomain from '@/assets/hero-bento-main.jpg';


const Hero = () => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Hero animates on mount (not scroll) since it's at the top
    const els = ref.current?.querySelectorAll('[data-anim], [data-reveal]');
    if (!els) return;

    els.forEach((el) => {
      const delay = parseInt((el as HTMLElement).dataset.delay ?? '0', 10);
      setTimeout(() => {
        el.classList.add('anim-visible');
        el.classList.add('is-visible');
      }, delay * 80); // 80ms per step = snappy stagger
    });
  }, []);

  return (
    <section ref={ref} className="bg-white pt-20 min-h-screen flex flex-col overflow-hidden">
      <div className="flex-1 max-w-[1400px] mx-auto w-full px-6 md:px-12 py-6 md:py-8 flex flex-col">

        {/* Top label */}
        <div data-anim data-delay="0" className="anim-item mb-10">
          <span className="inline-flex items-center gap-2.5 text-[10px] font-bold text-bteal uppercase tracking-[0.2em]">
            <span className="w-6 h-px bg-bteal" />
            Bihar Ethanol Association
            <span className="w-6 h-px bg-bteal" />
          </span>
        </div>

        {/* Main grid: text + bento */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_480px] xl:grid-cols-[1fr_560px] gap-10 xl:gap-16 items-start flex-1">

          {/* LEFT */}
          <div className="flex flex-col justify-between h-full">
            <div>
              {/* Giant headline — each word slides up independently */}
              <h1 className="text-[3.5rem] md:text-[4.5rem] xl:text-[5.5rem] font-extrabold text-ink leading-[0.92] tracking-tight mb-8">
                <div data-anim data-delay="1" className="anim-item overflow-hidden">
                  <span className="block">Powering</span>
                </div>
                <div data-anim data-delay="2" className="anim-item overflow-hidden">
                  <span className="block">Bihar&apos;s</span>
                </div>
                <div data-anim data-delay="3" className="anim-item overflow-hidden">
                  <span className="inline-flex items-end gap-3 flex-wrap">
                    <span className="relative inline-block">
                      <span className="relative z-10">Clean</span>
                      <span className="absolute bottom-1 left-0 w-full h-3 bg-byellow -z-0 rounded-sm" />
                    </span>
                    <span className="text-bteal">Energy</span>
                  </span>
                </div>
                <div data-anim data-delay="4" className="anim-item overflow-hidden">
                  <span className="block">Future</span>
                </div>
              </h1>

              {/* Descriptor row */}
              <div data-anim data-delay="5" className="anim-item flex items-start gap-4 mb-10 max-w-[500px]">
                <span className="w-0.5 h-16 bg-bgreen flex-shrink-0 mt-1 rounded-full" />
                <p className="text-ink-muted text-base leading-relaxed">
                  BEA is the unified platform connecting private distilleries, the Department of Industries, and Oil Marketing Companies to accelerate India&apos;s biofuel mission.
                </p>
              </div>

              {/* CTAs */}
              <div data-anim data-delay="6" className="anim-item flex flex-col sm:flex-row gap-3 mb-14">
                <Link
                  to="/ecosystem"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full bg-ink text-white font-semibold text-sm hover:bg-bteal transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  Explore Ecosystem
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line" /></span>
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full border-2 border-ink text-ink font-semibold text-sm hover:border-bteal hover:text-bteal transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  About BEA
                </Link>
              </div>
            </div>
          </div>

          {/* RIGHT: Bento grid — tiles animate in with stagger */}
          <div className="grid grid-cols-2 grid-rows-[280px_140px_140px] gap-3">
            {/* BIG image — spans 2 columns, 1 row */}
            <div data-reveal="scale" data-delay="7" className="col-span-2 rounded-2xl overflow-hidden relative">
              <img
                src={herobentomain}
                alt="Bihar Ethanol Plant"
                className="w-full h-full object-cover object-top"
              />
              {/* Overlay badge */}
              <div className="absolute bottom-3 left-3 bg-white/95 rounded-xl px-3 py-2 flex items-center gap-2.5">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-bgreen/10 flex-shrink-0">
                  <i className="ri-leaf-line text-bgreen text-xs" />
                </div>
                <div>
                  <p className="text-ink text-[10px] font-bold">E20 Mission</p>
                  <p className="text-ink-muted text-[9px]">20% ethanol blending goal</p>
                </div>
              </div>
            </div>

            {/* Teal tile */}
            <div data-reveal="scale" data-delay="8" className="rounded-2xl bg-bteal flex flex-col justify-between p-5">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20">
                <i className="ri-building-2-line text-white text-sm" />
              </div>
              <div>
                <p className="text-4xl font-extrabold text-white leading-none">22</p>
                <p className="text-white/75 text-xs mt-1.5 leading-snug">Active distilleries in Bihar</p>
              </div>
            </div>

            {/* Yellow tile */}
            <div data-reveal="scale" data-delay="9" className="rounded-2xl bg-byellow overflow-hidden relative flex flex-col justify-between p-5">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-black/10">
                <i className="ri-plant-line text-ink text-sm" />
              </div>
              <div>
                <p className="text-4xl font-extrabold text-ink leading-none">1.1B</p>
                <p className="text-ink/70 text-xs mt-1.5 leading-snug">Litres ethanol capacity</p>
              </div>
            </div>

            {/* Green wide tile */}
            <div data-reveal="scale" data-delay="10" className="col-span-2 rounded-2xl bg-bgreen flex items-center justify-between px-6 py-4 gap-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white/20 flex-shrink-0">
                  <i className="ri-government-line text-white text-base" />
                </div>
                <div>
                  <p className="text-white font-bold text-sm">Government Partnership</p>
                  <p className="text-white/70 text-xs mt-0.5">Directly linked with Bihar Dept. of Industries</p>
                </div>
              </div>
              <button
                onClick={() => alert('Coming Soon!')}
                className="flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white text-bgreen font-semibold text-xs hover:bg-byellow hover:text-ink transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Learn more <i className="ri-arrow-right-line text-xs" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-100" />
    </section>
  );
};

export default Hero;
